import { type CSSProperties } from 'react';

interface HeartProps {
  size?: number;
  variant?: 'fill' | 'outline' | 'sealed';
  className?: string;
  style?: CSSProperties;
  title?: string;
}

/**
 * Hand-drawn heart. The path is intentionally asymmetric — symmetric hearts
 * read as cartoon clip-art; tilting the right lobe ~3% gives it that
 * sketched-on-the-back-of-a-receipt feel.
 *
 * Variants:
 *   fill    — filled blush, soft rose stroke. The default.
 *   outline — line art only, used as a divider or subtle motif.
 *   sealed  — locked state: lavender mist with a wax-seal dotted ring.
 */
export function Heart({ size = 24, variant = 'fill', className, style, title }: HeartProps) {
  const stroke = variant === 'sealed' ? 'var(--c-locked-edge)' : 'var(--c-rose-deep)';
  const fill =
    variant === 'fill'    ? 'var(--c-blush)' :
    variant === 'sealed'  ? 'var(--c-locked)' :
                            'transparent';

  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      className={className} style={style} aria-label={title} role={title ? 'img' : 'presentation'}
    >
      {variant === 'sealed' && (
        <circle cx="12" cy="12" r="11" fill="none"
          stroke="var(--c-locked-edge)" strokeWidth="0.5"
          strokeDasharray="1.4 1.6" opacity="0.5" />
      )}
      <path
        d="M12 20.5
           c-2.2-1.5-7.5-4.6-9.2-9.0
           C1.4 7.4 3.6 4 7 4
           c1.9 0 3.7 1.0 5 2.5
           C13.4 5 15.2 4 17.1 4
           c3.3 0 5.6 3.5 4.4 7.1
           C19.7 16 14.4 19 12 20.5z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
